import React, { ReactElement } from "react";
import { Route, Switch } from "react-router-dom";
import { AutoQuoteDetailsPage } from "pages/autoQuote/AutoQuoteDetailsPage/AutoQuoteDetailsPage";
import HomePage from "../pages/HomePage/HomePage";
import ServiceInfoPage from "../pages/HomePage/SubPages/ServiceInfoPage";
import { SignUpPage } from "../pages/SignUpPage/SignUpPage";
import { CreateProjectFromExternalLinkPage } from "../pages/CreateProjectPage/CreateProjectFromExternalLinkPage/CreateProjectFromExternalLinkPage";
import { CreateProjectPage } from "../pages/CreateProjectPage/CreateProjectPage";
import AppPlaceholder from "../pages/HomePage/SubPages/AppPlaceholder";
import { EmailConfirmationPage } from "../pages/EmailConfirmationPage/EmailConfirmationPage";
import IFrameViewer from "../pages/HomePage/SubPages/IFrameViewer";
import { ChangePasswordPage } from "../pages/ChangePasswordPage/ChangePasswordPage";
import { ReportsPage } from "../pages/ReportsPage/ReportsPage";
import { ReportDetailsPage } from "../pages/ReportsPage/ReportDetailsPage/ReportDetailsPage";
import { PowerBiReportsPage } from "../pages/PowerBiReportsPage/PowerBiReportsPage";
import { PowerBiReportEmbedPage } from "../pages/PowerBiReportsPage/PowerBiReportEmbedPage/PowerBiReportEmbedPage";
import {
  MachineTranslationPage,
  MachineTranslationTabId
} from "../pages/MachineTranslationPage/MachineTranslationPage";
import { RestrictedPage } from "./RestrictedPage/RestrictedPage";
import { UserGroup } from "../gen/clients/llts";
import { CoupaStarterPage } from "../pages/coupa/CoupaStarterPage";
import { CreateCoupaQuotePage } from "../pages/coupa/CreateCoupaQuotePage/CreateCoupaQuotePage";
import { ErrorPage } from "./ErrorPage/ErrorPage";
import { CoupaErrorPage } from "../pages/coupa/CoupaErrorPage/CoupaErrorPage";
import { CoupaRequestsPage } from "../pages/coupa/CoupaRequestsPage/CoupaRequestsPage";
import { JobRequestDetailsPage } from "../pages/coupa/CoupaRequestsPage/JobRequestDetailsPage/JobRequestDetailsPage";
import { PageLayout } from "./PageLayout/PageLayout";
import LocaleManager from "./LocaleManager/LocaleManager";

const Routes = (): ReactElement => (
  <>
    <LocaleManager />
    <Switch>
      <Route path="/:lltsBranchId?/languageLineConnect">
        <PageLayout>
          <AppPlaceholder id="languageLineConnect" />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/quickTranslation">
        <PageLayout>
          <AppPlaceholder id="quickTranslation" />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/connector">
        <PageLayout>
          <AppPlaceholder id="connector" />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/websiteProxy">
        <PageLayout>
          <AppPlaceholder id="websiteProxy" />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/consoleHelp">
        <PageLayout>
          <IFrameViewer id="consoleHelp" fileName="Frequently-Asked-Questions.html" />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/languageConnect">
        <PageLayout>
          <ServiceInfoPage id="languageConnect" />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/webSitesEasyWay">
        <PageLayout>
          <ServiceInfoPage id="webSitesEasyWay" />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/anythingInBetween">
        <PageLayout>
          <ServiceInfoPage id="anythingInBetween" />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/solutionWizard">
        <PageLayout>
          <ServiceInfoPage id="solutionWizard" />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/signUp" exact={true}>
        <PageLayout>
          <SignUpPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/signUp/:clientId/:token" exact={true}>
        <PageLayout>
          <SignUpPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/confirmEmail" exact={false}>
        <PageLayout>
          <EmailConfirmationPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/createProject/:clientId/:token">
        <PageLayout>
          <CreateProjectFromExternalLinkPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/createProject" exact>
        <PageLayout>
          <CreateProjectPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/changePassword" exact={true}>
        <PageLayout>
          <ChangePasswordPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/reports" exact={true}>
        <PageLayout>
          <RestrictedPage
            allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
            requiredApplications={["reports"]}
          >
            <ReportsPage />
          </RestrictedPage>
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/reports/:reportId">
        <PageLayout>
          <RestrictedPage
            allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
            requiredApplications={["reports"]}
          >
            <ReportDetailsPage />
          </RestrictedPage>
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/pbiReports" exact={true}>
        <PageLayout>
          <RestrictedPage
            allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
            requiredApplications={["pbiReports"]}
          >
            <PowerBiReportsPage />
          </RestrictedPage>
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/pbiReports/:reportId">
        <PageLayout>
          <RestrictedPage
            allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
            requiredApplications={["pbiReports"]}
          >
            <PowerBiReportEmbedPage />
          </RestrictedPage>
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/machineTranslation" exact={true}>
        <PageLayout>
          <RestrictedPage
            allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
            requiredApplications={["machineTranslation"]}
          >
            <MachineTranslationPage />
          </RestrictedPage>
        </PageLayout>
      </Route>
      <Route path={`/:lltsBranchId?/machineTranslation/${MachineTranslationTabId.textTranslation}`}>
        <PageLayout>
          <RestrictedPage
            allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
            requiredApplications={["machineTranslation"]}
          >
            <MachineTranslationPage selectedTabId={MachineTranslationTabId.textTranslation} />
          </RestrictedPage>
        </PageLayout>
      </Route>
      <Route path={`/:lltsBranchId?/machineTranslation/${MachineTranslationTabId.fileTranslation}/:jobRequestId?`}>
        <PageLayout>
          <RestrictedPage
            allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
            requiredApplications={["machineTranslation"]}
          >
            <MachineTranslationPage selectedTabId={MachineTranslationTabId.fileTranslation} />
          </RestrictedPage>
        </PageLayout>
      </Route>
      <Route path={`/:lltsBranchId?/machineTranslation/${MachineTranslationTabId.mediaTranslation}/:jobRequestId?`}>
        <PageLayout>
          <RestrictedPage
            allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
            requiredApplications={["machineTranslation"]}
          >
            <MachineTranslationPage selectedTabId={MachineTranslationTabId.mediaTranslation} />
          </RestrictedPage>
        </PageLayout>
      </Route>
      <Route path={`/:lltsBranchId?/machineTranslation/${MachineTranslationTabId.aiToolbox}/:jobRequestId?`}>
        <PageLayout>
          <RestrictedPage
            allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
            requiredApplications={["machineTranslation"]}
          >
            <MachineTranslationPage selectedTabId={MachineTranslationTabId.aiToolbox} />
          </RestrictedPage>
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/coupa/starter/:requestId" exact={true}>
        <PageLayout>
          <CoupaStarterPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/coupa/createQuote" exact={true}>
        <PageLayout>
          <RestrictedPage allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]} requiredApplications={["coupa"]}>
            <CreateCoupaQuotePage />
          </RestrictedPage>
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/coupa/error" exact={true}>
        <PageLayout>
          <CoupaErrorPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/coupa/:jobRequestId" exact={true}>
        <PageLayout>
          <JobRequestDetailsPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/coupa/createQuote" exact={true}>
        <PageLayout>
          <ErrorPage apiError={{ message: "Invalid page URL" }} />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/coupa" exact={false}>
        <PageLayout>
          <CoupaRequestsPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?/quotes/:jobRequestId" exact={true}>
        <PageLayout>
          <AutoQuoteDetailsPage />
        </PageLayout>
      </Route>
      <Route path="/:lltsBranchId?">
        <PageLayout hideSignInButtonInHeader={true}>
          <HomePage id="homePage" />
        </PageLayout>
      </Route>
    </Switch>
  </>
);

export default Routes;
